<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard headline-id="cycle" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('cycle')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('cycle')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('cycle')"
                    @enter-edit-mode="onEnterEditMode('cycle', $t('cycle'))"
                    @leave-edit-mode="leaveEditMode('cycle')"
                    @discard-changes="discardChanges('cycle')"
                    @save="saveForm('cycle')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('cycle') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesCycleForm
                        v-if="showForm('cycle')"
                        ref="projectSeriesCycleFormRef"
                        class="p-5"
                        :series-id="series.id"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('cycle')"
                            :text="valueToString(series.cycle)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('notes-cycle')"
                            :text="valueToString(series.notesCycle)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="appointment" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('appointment')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('appointment')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('appointment')"
                    @enter-edit-mode="onEnterEditMode('appointment', $t('appointment'))"
                    @leave-edit-mode="leaveEditMode('appointment')"
                    @discard-changes="discardChanges('appointment')"
                    @save="saveForm('appointment')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('appointment') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesAppointmentForm
                        v-if="showForm('appointment')"
                        ref="projectSeriesAppointmentFormRef"
                        :series-id="series.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('project-notes-dayorder')"
                            :text="valueToString(series.notesDayOrder)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('project-notes-appointment')"
                            :text="valueToString(series.notesAppointment)"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="location" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('location')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('location')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('location')"
                    @enter-edit-mode="onEnterEditMode('location', $t('location'))"
                    @leave-edit-mode="leaveEditMode('location')"
                    @discard-changes="discardChanges('location')"
                    @save="saveForm('location')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('location') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <ProjectSeriesLocationForm
                        v-if="showForm('location')"
                        ref="projectSeriesLocationFormRef"
                        :series-id="series.id"
                        class="p-5"
                    />
                    <div v-else>
                        <CardRowText
                            :label="$t('location-requirements')"
                            :text="valueToString(series.locationRequirements)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('allocation-hints')"
                            :text="valueToString(series.allocationHints)"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { valueToString } from '@/forms/forms';
import { $t } from '@/plugins/fluent';
import { useProjectSeriesInjection } from '@/project/composables/useProjectSeriesInjection';
import ProjectSeriesAppointmentForm from '@/project/forms/series/ProjectSeriesAppointmentForm.vue';
import ProjectSeriesCycleForm from '@/project/forms/series/ProjectSeriesCycleForm.vue';
import ProjectSeriesLocationForm from '@/project/forms/series/ProjectSeriesLocationForm.vue';
import { useFluent } from 'fluent-vue';
import { onUnmounted, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const fluent = useFluent();
const { series } = useProjectSeriesInjection();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    getCardStatus,
} = useActiveCard();

const projectSeriesCycleFormRef = useTemplateRef('projectSeriesCycleFormRef');
const projectSeriesAppointmentFormRef = useTemplateRef('projectSeriesAppointmentFormRef');
const projectSeriesLocationFormRef = useTemplateRef('projectSeriesLocationFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('cycle', projectSeriesCycleFormRef);
register('appointment', projectSeriesAppointmentFormRef);
register('location', projectSeriesLocationFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
onUnmounted(() => resetActiveCard());
</script>
